import { useCallback, useMemo, useRef, useState } from 'react'
import { WorkItem } from '../../../types/Work/WorkItem'
import { useLazyLoader } from './useLazyLoader'

export type LazyWorkItemTree = {
	expandedIds: Set<number>
	childrenById: Map<number, WorkItem[]>
	loadingIds: Set<number>
	failedIds: Set<number>
	toggle: (workItem: WorkItem) => void
	expand: (workItemIds: number[], loadChildren?: boolean) => Promise<void>
	retry: (workItemId: number) => void
	reset: () => void
	/** Reloads open rows' children, keeping them open, and forgets closed rows' children. */
	refreshOpen: (loadUncached?: boolean) => Promise<void>
	closeUnder: (workItems: WorkItem[]) => void
}

/** Children load when a row opens, unless the work item already carries `childrenWorkItems`. */
export function useLazyWorkItemTree(
	fetchChildren: (workItemId: number) => Promise<WorkItem[]>,
): LazyWorkItemTree {
	const loader = useLazyLoader<WorkItem[]>(fetchChildren)
	const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
	const expandedIdsRef = useRef(expandedIds)
	expandedIdsRef.current = expandedIds
	const { load, peek, clear, evict, cachedIds } = loader

	const toggle = useCallback(
		(workItem: WorkItem) => {
			const opening = !expandedIdsRef.current.has(workItem.id)
			setExpandedIds((prev) => {
				const next = new Set(prev)
				if (opening) next.add(workItem.id)
				else {
					// Closing a row also closes the rows under it.
					const close = (item: WorkItem) => {
						if (!next.delete(item.id)) return
						;(item.childrenWorkItems ?? peek(item.id) ?? []).forEach(close)
					}
					close(workItem)
				}
				return next
			})
			if (opening && !workItem.childrenWorkItems && workItem.hasChildren)
				load(workItem.id).catch(() => undefined)
		},
		[load, peek],
	)

	const expand = useCallback(
		async (workItemIds: number[], loadChildren = true) => {
			setExpandedIds((prev) => new Set([...Array.from(prev), ...workItemIds]))
			if (!loadChildren) return
			for (const id of workItemIds) {
				await load(id).catch(() => undefined)
			}
		},
		[load],
	)

	const retry = useCallback(
		(workItemId: number) => {
			load(workItemId, true).catch(() => undefined)
		},
		[load],
	)

	const reset = useCallback(() => {
		clear()
		setExpandedIds(new Set())
	}, [clear])

	const refreshOpen = useCallback(
		async (loadUncached = false) => {
			const openIds = expandedIdsRef.current
			evict(cachedIds().filter((id) => !openIds.has(id)))
			const reloadIds = Array.from(openIds).filter((id) => loadUncached || peek(id))
			await Promise.all(reloadIds.map((id) => load(id, !!peek(id)).catch(() => undefined)))
		},
		[evict, cachedIds, peek, load],
	)

	const closeUnder = useCallback(
		(workItems: WorkItem[]) => {
			if (workItems.length === 0) return
			setExpandedIds((prev) => {
				const next = new Set(prev)
				const close = (item: WorkItem) => {
					if (!next.delete(item.id)) return
					;(item.childrenWorkItems ?? peek(item.id) ?? []).forEach(close)
				}
				workItems.forEach(close)
				return next.size === prev.size ? prev : next
			})
		},
		[peek],
	)

	return useMemo(
		() => ({
			expandedIds,
			childrenById: loader.loaded,
			loadingIds: loader.loadingIds,
			failedIds: loader.failedIds,
			toggle,
			expand,
			retry,
			reset,
			refreshOpen,
			closeUnder,
		}),
		[
			expandedIds,
			loader.loaded,
			loader.loadingIds,
			loader.failedIds,
			toggle,
			expand,
			retry,
			reset,
			refreshOpen,
			closeUnder,
		],
	)
}
